import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Alert,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Toolbar,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  FitnessCenter as FitnessIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Description as DescriptionIcon,
  FileDownload as FileDownloadIcon,
  Assignment as AssignmentIcon,
  SportsTennis as TennisIcon,
  SportsBasketball as BasketballIcon,
  SportsSoccer as FootballIcon,
  SportsVolleyball as VolleyballIcon,
  SportsCricket as CricketIcon,
  SportsHandball as BadmintonIcon,
  Sports as SportsIcon,
  Person as PersonIcon,
  ArrowBack as ArrowBackIcon,
  Menu as MenuIcon,
  ExitToApp as LogoutIcon,
} from '@mui/icons-material';
import { useAuthStore } from '@store/authStore';
import { useNavigate, useLocation } from 'react-router-dom';
import AdminService, { Player } from '@services/adminService';
import BasicPerformanceTracking from '@components/performance/BasicPerformanceTracking';
import TrainingHomeworkPanel from '@components/performance/TrainingHomeworkPanel';
import TrainingContentPanel from '@components/performance/TrainingContentPanel';
import ViewReportsView from '@components/performance/ViewReportsView';
import ManualAssessmentFormsView from '@components/performance/ManualAssessmentFormsView';
const DRAWER_WIDTH = 280;

const CoachPlayerTrainingPage: React.FC = () => {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [myStudents, setMyStudents] = useState<Player[]>([]);
  const [filteredPlayers, setFilteredPlayers] = useState<Player[]>([]);
  const [selectedPlayer, setSelectedPlayer] = useState<string>('');
  const [coachId, setCoachId] = useState<string>('');
  const [expandedSports, setExpandedSports] = useState<string[]>(['Badminton']);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Get player ID and sport/assessment from URL
  const urlParams = new URLSearchParams(location.search);
  const playerIdFromUrl = urlParams.get('player');
  const pathParts = location.pathname.split('/').filter(Boolean);
  const trainingIndex = pathParts.indexOf('player-training');
  const sportFromPath = trainingIndex >= 0 && pathParts.length > trainingIndex + 1 ? pathParts[trainingIndex + 1] : '';
  const assessmentTypeFromPath = trainingIndex >= 0 && pathParts.length > trainingIndex + 2 ? pathParts[trainingIndex + 2] : '';

  // Available sports
  const availableSports = ['Badminton', 'Cricket', 'Tennis', 'Football', 'Athletics'];
  const comingSoonSports = ['Cricket', 'Tennis', 'Football', 'Athletics'];

  useEffect(() => {
    if (user) {
      loadMyStudents();
    }
  }, [user]);

  // Filter players by selected sport
  // Don't filter by sport - show all players (like it worked before V2)
  // The sport filtering was causing issues when players don't have sport set correctly
  useEffect(() => {
    // Always show all players - don't filter by sport
    // This matches the behavior before V2 implementation
    setFilteredPlayers(myStudents);
  }, [myStudents]);

  useEffect(() => {
    // Priority 1: If playerIdFromUrl exists, use it (but wait for filteredPlayers to be populated)
    if (playerIdFromUrl) {
      // Wait for filteredPlayers to be populated
      if (filteredPlayers.length === 0 && myStudents.length > 0) {
        // Still filtering, wait
        return;
      }
      
      if (filteredPlayers.length > 0) {
        // Check if the player from URL exists in filteredPlayers
        const playerExists = filteredPlayers.find(p => p.id === playerIdFromUrl);
        if (playerExists) {
          // Player exists in filtered list, set it
          if (selectedPlayer !== playerIdFromUrl) {
            setSelectedPlayer(playerIdFromUrl);
          }
        } else {
          // Player from URL doesn't exist in filtered list
          // Check if player exists in myStudents (might be filtered out by sport)
          const playerInAllStudents = myStudents.find(p => p.id === playerIdFromUrl);
          if (playerInAllStudents) {
            // Player exists but doesn't match sport filter - select first available
            if (filteredPlayers.length > 0) {
              setSelectedPlayer(filteredPlayers[0].id);
              // Update URL to reflect the new selection
              if (sportFromPath && assessmentTypeFromPath) {
                navigate(`/coach/player-training/${sportFromPath}/${assessmentTypeFromPath}?player=${filteredPlayers[0].id}`, { replace: true });
              }
            }
          }
          // If player doesn't exist at all, keep current selection or leave empty
        }
      }
      return; // Don't proceed to auto-select if we have a URL player
    }
    
    // Priority 2: Auto-select first filtered player if none selected and we have filtered players
    // BUT only if we're on a page that requires a player (sport and assessment selected)
    if (sportFromPath && assessmentTypeFromPath && filteredPlayers.length > 0 && !selectedPlayer) {
      setSelectedPlayer(filteredPlayers[0].id);
      navigate(`/coach/player-training/${sportFromPath}/${assessmentTypeFromPath}?player=${filteredPlayers[0].id}`);
    } else if (filteredPlayers.length === 0 && selectedPlayer && myStudents.length > 0) {
      // If filteredPlayers is empty but we have students, it means filtering is in progress
      // Don't clear selectedPlayer yet - wait for filtering to complete
      return;
    } else if (filteredPlayers.length === 0 && selectedPlayer && myStudents.length === 0) {
      // No students at all, clear selection
      setSelectedPlayer('');
    }
  }, [playerIdFromUrl, filteredPlayers, selectedPlayer, myStudents, sportFromPath, assessmentTypeFromPath, navigate]);

  const loadMyStudents = async () => {
    let coachId = (user as any)?.coachId;
    
    // If coachId is not in user object, try to fetch it from the API
    if (!coachId && user?.id) {
      try {
        const response = await fetch(`/api/coaches/me?userId=${user.id}`);
        const data = await response.json();
        if (data.success && data.data) {
          coachId = data.data.id;
          // Update user object with coachId and other data
          const updatedUser = { 
            ...user, 
            coachId: data.data.id, 
            coachIdCode: data.data.coachIdCode,
            firstName: data.data.user.firstName,
            lastName: data.data.user.lastName,
          };
          const { setUser } = useAuthStore.getState();
          setUser(updatedUser);
        }
      } catch (err) {
        console.error('Error fetching coach data:', err);
      }
    }
    
    if (!coachId) {
      setError('Coach ID not found. Please contact support if this issue persists.');
      return;
    }
    setCoachId(String(coachId));

    setLoading(true);
    try {
      const response = await AdminService.getPlayersForCoach(coachId);
      const players = response.data || [];
      setMyStudents(players);
      
      // Don't set selectedPlayer here - let the useEffect handle it after filteredPlayers is set
      // This prevents the race condition where selectedPlayer is set but filteredPlayers is empty
    } catch (err: any) {
      console.error('Error loading students:', err);
      setError('Failed to load students');
      setMyStudents([]);
      setFilteredPlayers([]);
      setSelectedPlayer('');
    } finally {
      setLoading(false);
    }
  };

  const getSportIcon = (sport: string | null) => {
    if (!sport) return <SportsIcon />;
    switch (sport.toLowerCase()) {
      case 'badminton':
        return <BadmintonIcon />;
      case 'tennis':
        return <TennisIcon />;
      case 'football':
        return <FootballIcon />;
      case 'cricket':
        return <CricketIcon />;
      case 'athletics':
        return <FitnessIcon />;
      default:
        return <SportsIcon />;
    }
  };

  const handleToggleSport = (sport: string) => {
    if (comingSoonSports.includes(sport)) {
      return;
    }
    setExpandedSports(prev =>
      prev.includes(sport)
        ? prev.filter(s => s !== sport)
        : [...prev, sport]
    );
  };

  const handleAssessmentClick = (sport: string, assessmentType: string) => {
    if (comingSoonSports.includes(sport)) {
      return;
    }
    // Navigate to the assessment page - player can be selected after
    if (selectedPlayer) {
      navigate(`/coach/player-training/${sport}/${assessmentType}?player=${selectedPlayer}`);
    } else {
      navigate(`/coach/player-training/${sport}/${assessmentType}`);
    }
  };

  const handlePlayerChange = (playerId: string) => {
    setSelectedPlayer(playerId);
    if (sportFromPath && assessmentTypeFromPath) {
      navigate(`/coach/player-training/${sportFromPath}/${assessmentTypeFromPath}?player=${playerId}`);
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleBack = () => {
    navigate('/coach/dashboard');
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // New training model: one Skill Assessment parent with 4 levels.
  // All 4 levels currently reuse the existing low-level design/data.
  const skillAssessmentLevels = [
    { id: 'skill-assessment-beginner', label: 'Beginner', icon: <AssessmentIcon /> },
    { id: 'skill-assessment-intermediate', label: 'Intermediate', icon: <AssessmentIcon /> },
    { id: 'skill-assessment-advanced', label: 'Advanced', icon: <AssessmentIcon /> },
    { id: 'skill-assessment-pro', label: 'Pro', icon: <AssessmentIcon /> },
  ];
  const trainingUtilityItems = [
    { id: 'self-practice', label: 'Self-Practice', icon: <SportsIcon /> },
    { id: 'joy-fun-learning', label: 'Enjoyment and Entertainment', icon: <SportsIcon /> },
    { id: 'homework', label: 'Homework', icon: <AssignmentIcon /> },
    { id: 'submitted-reports', label: 'Submitted Reports', icon: <DescriptionIcon /> },
    { id: 'manual-assessment-forms', label: 'Manual Assessment Forms', icon: <FileDownloadIcon /> },
  ];

  const drawer = (
    <Box sx={{ bgcolor: '#e8f4f8', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Toolbar>
        <Typography variant="h6" noWrap component="div">
          Player Training
        </Typography>
      </Toolbar>
      <List>
        <ListItem disablePadding>
          <ListItemButton 
            onClick={handleBack}
            sx={{
              color: '#1976d2',
              '&:hover': {
                bgcolor: '#e3f2fd',
              }
            }}
          >
            <ListItemIcon sx={{ color: '#1976d2' }}>
              <ArrowBackIcon />
            </ListItemIcon>
            <ListItemText primary="Back to Dashboard" />
          </ListItemButton>
        </ListItem>
      </List>

      <List>
        {/* Sports as Parent Items */}
        {availableSports.map((sport) => {
          const isComingSoon = comingSoonSports.includes(sport);
          
          return (
            <React.Fragment key={sport}>
              <ListItem disablePadding>
                <ListItemButton 
                  onClick={() => !isComingSoon && handleToggleSport(sport)}
                  disabled={isComingSoon}
                  sx={{
                    opacity: isComingSoon ? 0.5 : 1,
                    cursor: isComingSoon ? 'default' : 'pointer',
                    '&:hover': {
                      bgcolor: isComingSoon ? 'transparent' : 'action.hover',
                    },
                    '&.Mui-disabled': {
                      opacity: 0.5,
                    },
                  }}
                >
                  <ListItemIcon sx={{ color: isComingSoon ? '#9e9e9e' : '#1976d2' }}>
                    {getSportIcon(sport)}
                  </ListItemIcon>
                  <ListItemText 
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography
                          sx={{ 
                            fontSize: '0.9rem', 
                            fontWeight: 700,
                            color: isComingSoon ? '#9e9e9e' : '#1976d2'
                          }}
                        >
                          {sport}
                        </Typography>
                        {isComingSoon && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#9e9e9e',
                              fontStyle: 'italic',
                              fontSize: '0.75rem',
                            }}
                          >
                            (Coming Soon)
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                  {!isComingSoon && (
                    expandedSports.includes(sport) ? (
                      <ExpandLessIcon sx={{ color: '#1976d2' }} />
                    ) : (
                      <ExpandMoreIcon sx={{ color: '#1976d2' }} />
                    )
                  )}
                </ListItemButton>
              </ListItem>
              {!isComingSoon && (
                <Collapse in={expandedSports.includes(sport)} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    <ListItem sx={{ pl: 4, py: 0.25 }}>
                      <ListItemText
                        primary="Skill Assessment"
                        primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 700, color: '#1976d2' }}
                      />
                    </ListItem>
                    {skillAssessmentLevels.map((assessment) => {
                      const isSelected = sportFromPath === sport && assessmentTypeFromPath === assessment.id;
                      return (
                        <ListItemButton
                          key={assessment.id}
                          sx={{ pl: 4 }}
                          selected={isSelected}
                          onClick={() => handleAssessmentClick(sport, assessment.id)}
                          disabled={comingSoonSports.includes(sport)}
                        >
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            {React.cloneElement(assessment.icon, { 
                              sx: { fontSize: 16, color: isSelected ? '#1976d2' : '#666' } 
                            })}
                          </ListItemIcon>
                          <ListItemText 
                            primary={assessment.label}
                            primaryTypographyProps={{ 
                              fontSize: '0.85rem', 
                              fontWeight: isSelected ? 700 : 600,
                              color: isSelected ? '#1976d2' : 'inherit'
                            }}
                          />
                        </ListItemButton>
                      );
                    })}
                    {trainingUtilityItems.map((assessment) => {
                      const isSelected = sportFromPath === sport && assessmentTypeFromPath === assessment.id;
                      return (
                        <ListItemButton
                          key={assessment.id}
                          sx={{ pl: 4 }}
                          selected={isSelected}
                          onClick={() => handleAssessmentClick(sport, assessment.id)}
                          disabled={comingSoonSports.includes(sport)}
                        >
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            {React.cloneElement(assessment.icon, { 
                              sx: { fontSize: 16, color: isSelected ? '#1976d2' : '#666' } 
                            })}
                          </ListItemIcon>
                          <ListItemText 
                            primary={assessment.label}
                            primaryTypographyProps={{ 
                              fontSize: '0.85rem', 
                              fontWeight: isSelected ? 700 : 600,
                              color: isSelected ? '#1976d2' : 'inherit'
                            }}
                          />
                        </ListItemButton>
                      );
                    })}
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          );
        })}
      </List>
      <Box sx={{ mt: 'auto', borderTop: '1px solid rgba(0, 0, 0, 0.12)' }}>
        <ListItem disablePadding>
          <ListItemButton 
            onClick={handleLogout}
            sx={{
              color: '#d32f2f',
              '&:hover': {
                bgcolor: '#ffebee',
              },
            }}
          >
            <ListItemIcon sx={{ color: '#d32f2f' }}>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText 
              primary="Logout"
              primaryTypographyProps={{ 
                fontSize: '0.9rem', 
                fontWeight: 600,
                color: '#d32f2f'
              }}
            />
          </ListItemButton>
        </ListItem>
      </Box>
    </Box>
  );

  const selectedPlayerData = filteredPlayers.find(p => p.id === selectedPlayer);

  const renderContent = () => {
    // If no sport or assessment selected, show default message (like My Training page)
    if (!sportFromPath || !assessmentTypeFromPath) {
      return (
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="h5" gutterBottom>
            Welcome to Player Training
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Select a sport and assessment type from the sidebar to get started.
          </Typography>
        </Box>
      );
    }

    // If sport and assessment are selected but no player, show message
    if (!selectedPlayer) {
      return (
        <Alert severity="info">
          Please select a player from the dropdown to view their training data.
        </Alert>
      );
    }

    if (!selectedPlayerData) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      );
    }

    const trainingLevelMap: Record<string, 'beginner' | 'intermediate' | 'advanced' | 'pro'> = {
      'skill-assessment-beginner': 'beginner',
      'skill-assessment-intermediate': 'intermediate',
      'skill-assessment-advanced': 'advanced',
      'skill-assessment-pro': 'pro',
    };
    const selectedTrainingLevel = trainingLevelMap[assessmentTypeFromPath || ''];

    // Map assessment types to components
    if (assessmentTypeFromPath === 'submitted-reports') {
      return (
        <ViewReportsView
          player={selectedPlayerData}
          coachId={coachId}
          selectedSport={sportFromPath}
        />
      );
    }

    if (assessmentTypeFromPath === 'manual-assessment-forms') {
      return (
        <ManualAssessmentFormsView
          player={selectedPlayerData}
          selectedSport={sportFromPath}
        />
      );
    }
    if (assessmentTypeFromPath === 'homework') {
      return (
        <TrainingHomeworkPanel
          playerId={selectedPlayerData.id}
          coachId={coachId || undefined}
          selectedSport={sportFromPath}
          canAssign
        />
      );
    }
    if (assessmentTypeFromPath === 'self-practice') {
      return (
        <TrainingContentPanel
          selectedSport={sportFromPath}
          trainingLevel={selectedTrainingLevel}
          contentType="self-practice"
          title="Practice at Backyard/Home"
        />
      );
    }
    if (assessmentTypeFromPath === 'joy-fun-learning') {
      return (
        <TrainingContentPanel
          selectedSport={sportFromPath}
          trainingLevel={selectedTrainingLevel}
          contentType="joy-fun-learning"
            title="Enjoyment and Entertainment"
        />
      );
    }

    // For new level-based skill assessment, all levels reuse low-level design/data for now.
    if (
      [
        'skill-assessment-beginner',
        'skill-assessment-intermediate',
        'skill-assessment-advanced',
        'skill-assessment-pro',
        'skill-assessment-master',
        'skill-assessment-low-level',
        'skill-assessment-high-level',
        'fitness-assessment',
        'drill-assessment',
      ].includes(assessmentTypeFromPath)
    ) {
      return (
        <BasicPerformanceTracking
          player={selectedPlayerData}
          coachId={coachId}
          selectedCoach={coachId || 'self'}
          assessmentType="master-training"
          trainingLevel={selectedTrainingLevel}
          selectedSport={sportFromPath}
          availableSports={availableSports}
        />
      );
    }

    return (
      <Alert severity="info">
        Assessment type "{assessmentTypeFromPath}" is not yet implemented.
      </Alert>
    );
  };

  return (
    <Box sx={{ display: 'flex' }}>
      {/* Sidebar */}
      <Box
        component="nav"
        sx={{ width: { sm: DRAWER_WIDTH }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: DRAWER_WIDTH,
              bgcolor: '#e8f4f8'
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: DRAWER_WIDTH,
              bgcolor: '#e8f4f8'
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${DRAWER_WIDTH}px)` },
          mt: 0
        }}
      >
        <Container maxWidth="xl">
          {/* Player Selection at Top - Only show when sport and assessment are selected */}
          {sportFromPath && assessmentTypeFromPath && (
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
              <FormControl sx={{ minWidth: 300 }} size="small">
                <InputLabel>Select Player</InputLabel>
                <Select
                  value={filteredPlayers.find(p => p.id === selectedPlayer) ? selectedPlayer : ''}
                  label="Select Player"
                  onChange={(e) => handlePlayerChange(e.target.value)}
                  disabled={filteredPlayers.length === 0}
                >
                  {filteredPlayers.map((player) => {
                    const playerIdCode = player.playerIdCode || player.id;
                    const firstName = player.user?.firstName || '';
                    const lastName = player.user?.lastName || '';
                    const displayName = `${playerIdCode} - ${firstName} ${lastName}`.trim();
                    return (
                      <MenuItem key={player.id} value={player.id}>
                        {displayName}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              {filteredPlayers.length === 0 && !comingSoonSports.includes(sportFromPath) && (
                <Typography variant="body2" color="text.secondary">
                  No players found for {sportFromPath}
                </Typography>
              )}
            </Box>
          )}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          {renderContent()}
        </Container>
      </Box>
    </Box>
  );
};

export default CoachPlayerTrainingPage;

